import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'
import { AppSettings } from 'interfaces/app'

// Type definitions for the context
interface CollaboratorContextProps {
  children: ReactNode
  settings: AppSettings
}

type CollaboratorContextValue = {
  logoSrc: string
  showLogo: boolean
  requestId: string | null
  primaryColor: string | null
  allCollaborationRequestsPath: string
}

const CollaboratorContext = createContext({} as CollaboratorContextValue)

export function useCollaboratorContext(): CollaboratorContextValue {
  return useContext(CollaboratorContext)
}

function getRequestIdFromUrl(): string | null {
  const exp = /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/
  const match = window.location.pathname.match(exp)
  return match ? match[0] : null
}

export default function CollaboratorContextProvider({ children, settings }: CollaboratorContextProps): ReactElement {
  const showLogo = useMemo(() => {
    const hasCustomLogo = !!settings?.customBranding?.logo
    return !settings?.customBranding || hasCustomLogo
  }, [settings?.customBranding])

  const logoSrc = useMemo(() => {
    if (!showLogo) {
      return ''
    }
    return settings?.customBranding?.logo || '/images/icons/brand-circle.svg'
  }, [showLogo, settings?.customBranding?.logo])

  const primaryColor = useMemo(() => {
    return settings?.customBranding?.primaryColor || null
  }, [settings?.customBranding?.primaryColor])

  const requestId = useMemo(() => {
    return getRequestIdFromUrl()
  }, [])

  const allCollaborationRequestsPath = useMemo(() => {
    return `/shared/${requestId}`
  }, [requestId])

  const contextValue: CollaboratorContextValue = {
    logoSrc,
    showLogo,
    requestId,
    primaryColor,
    allCollaborationRequestsPath,
  }

  return <CollaboratorContext.Provider value={contextValue}>{children}</CollaboratorContext.Provider>
}
